import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Trash2, Loader2, AlertCircle, ArrowDown, GripVertical } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AccessSequences = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('pricing');
  const [viewItem, setViewItem] = useState(null);
  const [accessSequences, setAccessSequences] = useState([]);
  const [conditionTables, setConditionTables] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSequence, setEditingSequence] = useState(null);
  const [formData, setFormData] = useState({
    access_sequence_id: '',
    description: '',
    steps: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [seqRes, tablesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/pricing/access-sequences`),
        axios.get(`${BACKEND_URL}/api/pricing/condition-tables`)
      ]);
      setAccessSequences(seqRes.data || []);
      setConditionTables(tablesRes.data || []);
    } catch (error) {
      console.error('Error loading access sequences:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load access sequences: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (seq = null) => {
    if (seq) {
      setEditingSequence(seq);
      // Sort steps by step_number when loading
      const sortedSteps = [...(seq.steps || [])].sort((a, b) => a.step_number - b.step_number);
      setFormData({
        access_sequence_id: seq.access_sequence_id,
        description: seq.description,
        steps: sortedSteps
      });
    } else {
      setEditingSequence(null);
      setFormData({
        access_sequence_id: '',
        description: '',
        steps: [{ step_number: 10, condition_table_id: '', is_exclusive: false }]
      });
    }
    setIsDialogOpen(true);
  };

  const handleAddStep = () => {
    const lastStep = formData.steps[formData.steps.length - 1];
    const newStepNumber = lastStep ? lastStep.step_number + 10 : 10;
    setFormData({
      ...formData,
      steps: [...formData.steps, { step_number: newStepNumber, condition_table_id: '', is_exclusive: false }]
    });
  };

  const handleRemoveStep = (index) => {
    if (formData.steps.length <= 1) {
      toast.error('Access sequence must have at least one step');
      return;
    }
    const newSteps = formData.steps.filter((_, i) => i !== index);
    setFormData({ ...formData, steps: newSteps });
  };

  const handleStepChange = (index, field, value) => {
    const newSteps = [...formData.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setFormData({ ...formData, steps: newSteps });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.access_sequence_id || formData.access_sequence_id.trim().length === 0) {
      toast.error('Access Sequence ID is required');
      return;
    }
    if (formData.access_sequence_id.length > 10) {
      toast.error('Access Sequence ID must be max 10 characters');
      return;
    }
    if (!formData.description || formData.description.trim().length === 0) {
      toast.error('Description is required');
      return;
    }
    if (formData.steps.length === 0) {
      toast.error('At least one step is required');
      return;
    }
    
    // Validate each step has a condition table
    for (let i = 0; i < formData.steps.length; i++) {
      if (!formData.steps[i].condition_table_id) {
        toast.error(`Step ${i + 1}: Condition Table is required`);
        return;
      }
    }

    setIsSaving(true);
    try {
      // Sort steps by step_number before saving
      const sortedSteps = [...formData.steps].sort((a, b) => a.step_number - b.step_number);
      const data = {
        access_sequence_id: formData.access_sequence_id.toUpperCase().trim(),
        description: formData.description.trim(),
        steps: sortedSteps.map((step, index) => ({
          step_number: step.step_number || (index + 1) * 10,
          condition_table_id: step.condition_table_id,
          is_exclusive: step.is_exclusive || false
        }))
      };

      if (editingSequence) {
        await axios.put(`${BACKEND_URL}/api/pricing/access-sequences/${editingSequence.access_sequence_id}`, data);
        toast.success(`Access Sequence '${editingSequence.access_sequence_id}' updated successfully`);
      } else {
        await axios.post(`${BACKEND_URL}/api/pricing/access-sequences`, data);
        toast.success(`Access Sequence '${data.access_sequence_id}' created successfully`);
      }
      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving access sequence:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to save access sequence: ${errorMsg}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (seqId) => {
    if (!window.confirm(`Are you sure you want to delete Access Sequence '${seqId}'? This action cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${BACKEND_URL}/api/pricing/access-sequences/${seqId}`);
      toast.success(`Access Sequence '${seqId}' deleted successfully`);
      loadData();
    } catch (error) {
      console.error('Error deleting access sequence:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to delete access sequence: ${errorMsg}`);
    }
  };

  const getTableDescription = (tableId) => {
    const table = conditionTables.find(t => t.condition_table_id === tableId);
    return table?.description || tableId;
  };

  // Pre-compute sorted steps for each sequence so render doesn't re-sort on
  // every re-render (perf fix for code-review item).
  const sortedSequences = useMemo(
    () => accessSequences.map((seq) => ({
      ...seq,
      _sortedSteps: [...(seq.steps || [])].sort((a, b) => a.step_number - b.step_number),
    })),
    [accessSequences]
  );

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Access Sequences</h1>
            <p className="text-sm text-gray-600 mt-1">Define the search strategy for finding condition records</p>
          </div>
          {writable && (<Button onClick={() => handleOpenDialog()} data-testid="add-access-sequence-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Access Sequence
          </Button>)}
        </div>

        <Card className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
              <span className="ml-3 text-gray-600">Loading access sequences...</span>
            </div>
          ) : accessSequences.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No access sequences found.</p>
              <p className="text-sm text-gray-400 mt-1">Click "Add Access Sequence" to create one.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedSequences.map((seq) => (
                <div key={seq.access_sequence_id} className="border rounded-lg p-4 hover:bg-gray-50" data-testid={`access-seq-${seq.access_sequence_id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-lg">{seq.access_sequence_id}</h3>
                      <p className="text-gray-600">{seq.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <RecordActions doctype="pricing" onView={() => setViewItem(seq)} onEdit={() => handleOpenDialog(seq)} />
                    </div>
                  </div>
                  
                  {/* Steps */}
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-gray-500 mb-2">Search Steps:</p>
                    <div className="flex flex-wrap gap-2">
                      {seq._sortedSteps.map((step, index) => (
                        <div key={`step-${step.step_number}`} className="flex items-center gap-2">
                          <div className={`px-3 py-2 rounded-lg text-sm ${step.is_exclusive ? 'bg-orange-100 text-orange-800 border border-orange-300' : 'bg-gray-100 text-gray-700'}`}>
                            <span className="font-mono font-bold mr-2">{step.step_number}</span>
                            {step.condition_table_id} - {getTableDescription(step.condition_table_id)}
                            {step.is_exclusive && <span className="ml-2 text-xs bg-orange-200 px-1 rounded">Exclusive</span>}
                          </div>
                          {index < seq._sortedSteps.length - 1 && (
                            <ArrowDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingSequence ? 'Edit Access Sequence' : 'Create Access Sequence'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="access_sequence_id">Access Sequence ID * (max 10 chars)</Label>
                  <Input
                    id="access_sequence_id"
                    value={formData.access_sequence_id}
                    onChange={(e) => setFormData({...formData, access_sequence_id: e.target.value.toUpperCase()})}
                    placeholder="e.g., PR00, K004"
                    maxLength={10}
                    disabled={!!editingSequence}
                    className="uppercase"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="e.g., Price Access Sequence"
                    maxLength={100}
                    required
                  />
                </div>
              </div>

              {/* Steps */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Search Steps (in order of priority)</h3>
                  <Button type="button" size="sm" variant="outline" onClick={handleAddStep}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Step
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {formData.steps.map((step, index) => (
                    <div key={index} className="flex items-center gap-3 bg-white p-3 rounded border">
                      <GripVertical className="w-4 h-4 text-gray-400" />
                      <div className="w-20">
                        <Input
                          type="number"
                          value={step.step_number}
                          onChange={(e) => handleStepChange(index, 'step_number', parseInt(e.target.value) || 0)}
                          placeholder="10"
                          className="text-center"
                        />
                      </div>
                      <div className="flex-1">
                        <Select 
                          value={step.condition_table_id || '__SELECT__'} 
                          onValueChange={(v) => handleStepChange(index, 'condition_table_id', v === '__SELECT__' ? '' : v)}
                        >
                          <SelectTrigger><SelectValue placeholder="Select Condition Table" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__SELECT__">Select Condition Table</SelectItem>
                            {conditionTables.map(table => (
                              <SelectItem key={table.condition_table_id} value={table.condition_table_id}>
                                {table.condition_table_id} - {table.description}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <label className="flex items-center gap-2 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={step.is_exclusive}
                          onChange={(e) => handleStepChange(index, 'is_exclusive', e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Exclusive</span>
                      </label>
                      <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={() => handleRemoveStep(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Steps are searched in order. "Exclusive" means stop searching if a record is found at this step.
                </p>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t mt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSaving}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving} data-testid="submit-access-sequence">
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {editingSequence ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingSequence ? 'Update Access Sequence' : 'Create Access Sequence'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default AccessSequences;
